import React, { useState } from 'react';
import { X, Clock, CreditCard } from 'lucide-react';
import type { Organization } from '../types';

const FREE_CREDITS = 3;
const WARN_DAYS = 30;

function getDaysLeft(expiresAt: string): number {
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
}

function dismissKey(orgId: string, type: 'expiry' | 'credits'): string {
  return `sideoutiq_banner_dismissed_${type}_${orgId}`;
}

function isDismissed(orgId: string, type: 'expiry' | 'credits'): boolean {
  const raw = localStorage.getItem(dismissKey(orgId, type));
  if (!raw) return false;
  // Re-show after 7 days so it doesn't disappear forever
  return Date.now() - Number(raw) < 7 * 86_400_000;
}

function dismiss(orgId: string, type: 'expiry' | 'credits'): void {
  localStorage.setItem(dismissKey(orgId, type), String(Date.now()));
}

interface Props {
  org: Organization;
}

export default function BetaBanner({ org }: Props) {
  const hasActiveSub = org.stripeSubscriptionStatus === 'active' || org.stripeSubscriptionStatus === 'trialing';

  // --- Expiry banner ---
  const daysLeft = org.betaExpiresAt ? getDaysLeft(org.betaExpiresAt) : null;
  const showExpiry =
    !hasActiveSub &&
    daysLeft !== null &&
    daysLeft <= WARN_DAYS &&
    daysLeft > 0;

  // --- Credits banner ---
  const creditsUsed = org.matchCreditsUsed ?? 0;
  const showCredits = !hasActiveSub && creditsUsed >= FREE_CREDITS;

  const [expiryDismissed, setExpiryDismissed] = useState(() =>
    showExpiry ? isDismissed(org.id, 'expiry') : true
  );
  const [creditsDismissed, setCreditsDismissed] = useState(() =>
    showCredits ? isDismissed(org.id, 'credits') : true
  );

  if ((!showExpiry || expiryDismissed) && (!showCredits || creditsDismissed)) {
    return null;
  }

  const handleDismissExpiry = () => {
    dismiss(org.id, 'expiry');
    setExpiryDismissed(true);
  };

  const handleDismissCredits = () => {
    dismiss(org.id, 'credits');
    setCreditsDismissed(true);
  };

  return (
    <div className="space-y-2 mb-4">
      {showExpiry && !expiryDismissed && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
          <Clock className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="flex-1 text-amber-800">
            <strong>Beta access expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}.</strong>
            {' '}Upgrade to Pro to keep full access to all features.
          </span>
          <button
            type="button"
            onClick={() => { /* TODO: open upgrade flow when Stripe is wired */ }}
            className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            Upgrade to Pro
          </button>
          <button
            type="button"
            onClick={handleDismissExpiry}
            className="shrink-0 p-1 text-amber-500 hover:text-amber-700 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showCredits && !creditsDismissed && (
        <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
          <CreditCard className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="flex-1 text-blue-800">
            <strong>Your 3 free match credits have been used.</strong>
            {' '}Upgrade to Pro to continue tracking your season.
          </span>
          <button
            type="button"
            onClick={() => { /* TODO: open upgrade flow when Stripe is wired */ }}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            Upgrade to Pro
          </button>
          <button
            type="button"
            onClick={handleDismissCredits}
            className="shrink-0 p-1 text-blue-500 hover:text-blue-700 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
